// The Esc-stack manager (DESIGN_SPEC §6): Esc closes the innermost open layer only
// (new-preset modal → preferences drawer → constructor drawer → constructor → designer fullscreen).
import { useEffect, useRef } from 'react';

type Layer = { id: number; onEscape: () => void };
const stack: Layer[] = [];
let nextId = 1;
let listening = false;

function onKeyDown(e: KeyboardEvent) {
  if (e.key !== 'Escape' || e.defaultPrevented) return;
  const top = stack[stack.length - 1];
  if (!top) return;
  e.preventDefault();
  e.stopPropagation();
  top.onEscape();
}

function ensureListener() {
  if (listening || typeof window === 'undefined') return;
  window.addEventListener('keydown', onKeyDown);
  listening = true;
}

/** Registers a layer while `active`; the most recently opened active layer gets Esc. */
export function useEscapeLayer(active: boolean, onEscape: () => void): void {
  const handler = useRef(onEscape);
  useEffect(() => {
    handler.current = onEscape;
  });
  useEffect(() => {
    if (!active) return;
    ensureListener();
    const layer: Layer = { id: nextId++, onEscape: () => handler.current() };
    stack.push(layer);
    return () => {
      const i = stack.findIndex((l) => l.id === layer.id);
      if (i >= 0) stack.splice(i, 1);
    };
  }, [active]);
}

/** Number of open layers (tests and debugging). */
export function escapeDepth(): number {
  return stack.length;
}
